import {vec2, vec3, vec4, mat4} from 'gl-matrix';
import {clamp, lerp} from './utils';

const {PI, sqrt, exp, cos, sin, atan2, min, max, abs} = Math;

export function pipeline(...ops) {
    function apply(idx, value) {
        let op = ops[idx];
        if (!op)
            return;

        let next = v => apply(idx +  1, v);
        op(value, next);
        //next(result);
    }

    return function(value) {
        apply(0, value);
    }
}

export function makeStrokePipeline(output) {

    function DrawLine() {
        let lastPos;
        return function(pos, next) {
            if (!pos)
                return;

            if (lastPos) {
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(...lastPos);
                ctx.lineTo(...pos);
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 1;
                ctx.stroke();
                ctx.restore();
            }

            lastPos = [...pos];
        }
    }

    function DrawArrow() {
        return function(pos, next) {
            if (!pos)
                return;

            ctx.save();
            ctx.translate(pos[0], pos[1]);
            ctx.rotate(PI + pos[2]);
            ctx.scale(4, 7);

            ctx.beginPath();
            ctx.moveTo(-1, -1);
            ctx.lineTo(1, -1);
            ctx.lineTo(0, 1);

            ctx.fillStyle = 'white';
            ctx.fill();
            ctx.restore();
        }
    }

    function Spacing(spacing=50) {
        let travel = 0;
        let angle = 0;
        let lastPos;
        let totalDistance = 0;
        let P = new Array;

        return function(pos, next) {
            if (!pos) {
                next(lastPos);
                return;
            }

            if (lastPos) {
                let dy = pos[1] - lastPos[1];
                let dx = pos[0] - lastPos[0];
                if (abs(dy) > 0 || abs(dx) > 0) {
                    let theta = angle;
                    let theta1 = atan2(dx, -dy);
                    {
                        let q = PI/4;
                        if (theta > PI-q && theta1 < q-PI) {
                            theta -= 2*PI;
                        } else if (theta < q-PI && theta1 > PI-q) {
                            theta += 2*PI;
                        }
                    }
                    theta = lerp(theta, theta1, 0.01);
                    angle = theta;
                }
            }

            if (lastPos) {
                let dist = vec2.dist(lastPos, pos);
                while (travel < dist) {
                    let u = travel / dist;
                    //vec3.lerp(P, lastPos, pos, u);
                    const dim = pos.length;
                    for (let j = 0; j < dim; ++j) {
                        P[j] = lerp(lastPos[j], pos[j], u);
                    }
                    next([
                        ...P,
                        totalDistance,
                        angle,
                    ]);
                    travel += spacing;
                    totalDistance += spacing;
                }
                travel -= dist;
            }

            lastPos = [...pos];
        }
    }

    function Smoothing(sigma=20) {
        let points = [];
        return function(pos, next) {
            if (pos) {
                points.push(pos);
                let out = new Array(pos.length);
                let idx = points.length - 1;
                getSmoothedPoint(out, points, idx, sigma);
                next(out);
            } else {
                if (points.length)
                    next(points.at(-1));
            }
        }
    }

    function DrawPoint(color, size=1) {
        return (pos, next) => {
            if (pos) {
                ctx.fillStyle = color;
                let [x, y] = pos
                ctx.fillRect(x-size/2, y-size/2, size, size);
            }
            next(pos);
        };
    }

    function MakeCurve() {
        let points = [];

        // input points
        let p0 = new Array;
        let p1 = new Array;
        let p2 = new Array;
        let p3 = new Array;

        // bezier points
        // let b0 = new Array(3);
        // let b1 = new Array(3);
        // let b2 = new Array(3);
        // let b3 = new Array(3);

        return (pos, next) => {
            if (!pos) {
                next();
                return;
            }

            points.push([...pos]);

            if (points.length > 4)
                points.shift();

            if (points.length > 0) {
                let i = points.length - 2;
                getPoint(p0, points, i - 1);
                getPoint(p1, points, i);
                getPoint(p2, points, i + 1);
                getPoint(p3, points, i + 2);

                // calc the bezier points
                // vec2.copy(b0, p1);
                // vec2.copy(b3, p2);
                // for (let j = 0; j < 2; ++j) {
                //     b1[j] = -p0[j]/6 + p1[j] + p2[j]/6;
                //     b2[j] =  p1[j]/6 + p2[j] - p3[j]/6;
                // }

                //next([b0, b1, b2, b3]);
                next([ p0, p1, p2, p3 ]);
            }
        };
    }

    function DrawCurve() {
        return cps => {
            if (!cps)
                return;

            ctx.save();

            if (0) {
                ctx.beginPath();
                ctx.moveTo(...cps[0]);
                ctx.lineTo(...cps[3]);
                ctx.strokeStyle = 'red';
                ctx.lineWidth = 1;
                ctx.stroke();
            }

            ctx.beginPath();
            ctx.moveTo(...cps[0]);
            ctx.bezierCurveTo(
                ...cps[1],
                ...cps[2],
                ...cps[3]);
            ctx.strokeStyle = 'orange';
            ctx.lineWidth = 1;
            ctx.stroke();

            ctx.restore();
        };
    }

    function LinearizeCurve() {
        let P = new Array;
        let first = true;
        return (cps, next) => {
            if (first) {
                next([...cps[1]]);
                first = false;
            }
            let n = 16;
            for (let i = 0; i < n; ++i) {
                let u = (i + 1) / n;
                let uu = u * u;
                let uuu = u * uu;
                let t = .5;

                let b0 = -uuu + 2*uu - u;
                let b1 = 3*uuu - 5*uu + 2;
                let b2 = -3*uuu + 4*uu + u;
                let b3 = uuu - uu;

                const dim = cps[0].length;
                for (let j = 0; j < dim; ++j) {
                    P[j] = 0.5 * (
                        b0 * cps[0][j] +
                        b1 * cps[1][j] +
                        b2 * cps[2][j] +
                        b3 * cps[3][j]);

                    // P[j] = (
                    //     -t*u + 2*t*uu - t*uuu * cps[0][j] +
                    //     1 + (t-3)*uu + (2-t)*uuu * cps[1][j] +
                    //     t*u + (3-2*t)*uu + (t-2)*uuu * cps[2][j] +
                    //     -t*uu + t*uuu * cps[3][j]);
                }
                next([...P]);
            }
        };
    }

    return pipeline(
        Smoothing(10),
        MakeCurve(),
        LinearizeCurve(),
        Spacing(2),
        output,
    );

    return pipeline(
        Smoothing(50),
        //Spacing(10),
        //DrawPoint('#f00', 3),
        //DrawLine(),
        MakeCurve(),
        LinearizeCurve(),
        Spacing(2),
        output,
        //DrawLine(),
        //DrawArrow(),
        //DrawPoint('#0ff', 1),

        // Spacing(100),
        // DrawPoint('#ff0', 4),
        // MakeCurve(),
        // LinearizeCurve(),
        // Spacing(20),
        // DrawPoint('#f0f', 1),
    );
}

function getSmoothedPoint(out, points, idx, sigma=20) {
    let totalDist = 0;
    let totalWeight = 0;
    let epsilon = 1e-3;
    //vec3.set(out, 0, 0, 0);
    out.fill(0);
    for (let i = idx; i >= 0; --i) {
        let p = points[i];

        let weight = gaussian(totalDist, sigma);
        if (weight < epsilon)
            break;

        // add weighted position
        //vec3.scaleAndAdd(out, out, p, weight);
        for (let j = 0; j < out.length; ++j) {
            out[j] += weight * p[j];
        }

        // accumulate weight
        totalWeight += weight;

        // accumulate distance
        if (i < idx) {
            let q = points[i + 1];
            let dist = vec2.dist(q, p);
            totalDist += dist;
        }

        // XXX break if weight < EPSILON
    }

    // normalize
    if (totalWeight > 0) {
        //vec3.scale(out, out, 1 / totalWeight);
        for (let j = 0; j < out.length; ++j) {
            out[j] /= totalWeight;
        }
    }

    return out;
}

// copy points[i] to out, where i is clamped to 0,points.length-1
// out is an array and will become Array(point.length)
function getPoint(out, points, i) {
    i = clamp(i, 0, points.length - 1);
    const point = points[i];
    //vec3.copy(out, points[i]);
    for (let j = 0; j < point.length; ++j)
        out[j] = point[j];
}

function gaussian(x, sigma) {
    let w = 1 / (sigma * sqrt(2 * PI));
    return exp(-0.5 * (x * x) / (sigma * sigma));
}
